import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import produce from 'immer'

import {
  CheckConsignmentOrDeliveryNumber,
  CheckDate1isBeforeDate2,
  CheckGivenSupplierName,
  CheckNotes,
  CheckReceiverOrSupplierOrSenderOrCarrierReference,
  CheckTradeContractor,
  CheckVehicleReg
} from 'portal/lib/functions/SimpleDataValidation'
import {
  SubSection,
  SubSectionHeading
} from 'portal/components/RecordsModules/common/StyledComponents'

import { ClientKnownAddressInput } from 'portal/components/RecordsModules/Edit/ClientKnownAddressInput'
import { useInfractionGetter } from 'portal/store/infractions/InfractionsContext'
import { dateValidator } from 'portal/lib/primitives/forms/Validators'
import {
  FormInput,
  FormItem,
  ErrorContainer
} from 'portal/components/RecordEditing'
import { Input, Checkbox } from 'portal/lib/primitives'
import { FormInfraction } from 'portal/components/RecordEditing/FormInfraction'

const BES6001 = ';BES 6001'
const ISO14001 = ';ISO 14001'
const FSC = ';FSC'
const PEFC = ';PEFC'
const CARES = ';CARES'

export function Delivery() {
  const form = useFormContext()

  const getInfraction = useInfractionGetter()
  const supplierCertificateInfractions = getInfraction(
    'data.givenSupplierCertifications',
    { isPrefix: true }
  )

  const dataSourceTracking = useSelector(
    state => state.records.details.item.dataSourceTracking
  )

  const recordData = useSelector(state => state.records.details.item.data)

  const [bes6001Cert, setBes6001Cert] = React.useState(
    recordData?.givenSupplierCertifications?.includes('BES 6001') || false
  )
  const [iso14001Cert, setIso14001Cert] = React.useState(
    recordData?.givenSupplierCertifications?.includes('ISO 14001') || false
  )
  const [fscCert, setFscCert] = React.useState(
    recordData?.givenSupplierCertifications?.includes('FSC') || false
  )
  const [pefcCert, setPefcCert] = React.useState(
    recordData?.givenSupplierCertifications?.includes('PEFC') || false
  )
  const [caresCert, setCaresCert] = React.useState(
    recordData?.givenSupplierCertifications?.includes('CARES') || false
  )

  useEffect(
    () => {
      form.register('data.BES6001')
      form.setValue('data.BES6001', bes6001Cert)
      form.register('data.ISO14001')
      form.setValue('data.ISO14001', iso14001Cert)
      form.register('data.FSC')
      form.setValue('data.FSC', fscCert)
      form.register('data.PEFC')
      form.setValue('data.PEFC', pefcCert)
      form.register('data.CARES')
      form.setValue('data.CARES', caresCert)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form]
  )

  return (
    <>
      <SubSection style={{ marginTop: '1.25rem' }}>
        <SubSectionHeading>Delivery</SubSectionHeading>
        <FormItem
          label="Arrival date and time"
          valueAdd={{
            fieldName: 'deliveryArrivalDate',
            ...dataSourceTracking?.deliveryArrivalDate
          }}
        >
          <FormInput.DateTimeCells
            name="data.deliveryArrivalDate"
            rules={dateValidator(
              form.getValues,
              'data.deliveryArrivalDate',
              true,
              'Arrival date and time'
            )}
          />
        </FormItem>
        <FormItem
          label="Exit date and time"
          valueAdd={{
            fieldName: 'deliveryExitDate',
            ...dataSourceTracking?.deliveryExitDate
          }}
        >
          <FormInput.DateTimeCells
            name="data.deliveryExitDate"
            rules={dateValidator(form.getValues, 'data.deliveryExitDate')}
            simpleDataValidationRules={v =>
              CheckDate1isBeforeDate2(
                'Arrival date and time',
                form.getValues('data.deliveryArrivalDate'),
                'Exit date and time',
                form.getValues('data.deliveryExitDate')
              )
            }
          />
        </FormItem>
        <FormItem
          label="Delivery ID"
          valueAdd={{
            fieldName: 'deliveryId',
            ...dataSourceTracking?.deliveryId
          }}
        >
          <FormInput.Text
            label="Delivery ID"
            name="data.deliveryId"
            autoFocus
            rules={{
              required:
                '"Save" is only available if there is a valid Delivery ID. Please enter one before continuing'
            }}
            simpleDataValidationRules={CheckConsignmentOrDeliveryNumber}
          />
        </FormItem>
        <ClientKnownAddressInput
          addressName="Site"
          form={form}
          knownlabel={'Site address'}
          knownPropertyPath={'data.deliveryAddressKnown'}
          enteredPropertyPath={'data.deliveryAddressEntered'}
          coordinatesPropertyPath={'data.deliveryAddressCoordinates'}
          coordinatesOfOtherAddressPropertyPath={
            'data.dispatchAddressCoordinates'
          }
          valueAdd={{
            fieldName: 'deliveryAddressKnown',
            ...dataSourceTracking?.deliveryAddressKnown
          }}
          otherAddressName="Dispatch"
        />
        <ClientKnownAddressInput
          addressName="Dispatch"
          form={form}
          knownlabel={'Dispatch address'}
          knownPropertyPath={'data.dispatchAddressKnown'}
          enteredPropertyPath={'data.dispatchAddressEntered'}
          coordinatesPropertyPath={'data.dispatchAddressCoordinates'}
          coordinatesOfOtherAddressPropertyPath={
            'data.deliveryAddressCoordinates'
          }
          valueAdd={{
            fieldName: 'dispatchAddressKnown',
            ...dataSourceTracking?.dispatchAddressKnown
          }}
          otherAddressName="Site"
        />
        <FormItem
          label="Receiver reference"
          valueAdd={{
            fieldName: 'receiverReferenceNumber',
            ...dataSourceTracking?.receiverReferenceNumber
          }}
        >
          <FormInput.Text
            label="Receiver reference"
            name="data.receiverReferenceNumber"
            simpleDataValidationRules={
              CheckReceiverOrSupplierOrSenderOrCarrierReference
            }
          />
        </FormItem>
      </SubSection>
      <SubSection style={{ marginTop: '1.25rem' }}>
        <SubSectionHeading>Supplier</SubSectionHeading>
        <FormItem
          label="Company name"
          valueAdd={{
            fieldName: 'knownSupplierName',
            ...dataSourceTracking?.knownSupplierName
          }}
        >
          <FormInput.Text
            label="Company name"
            name="data.knownSupplierName"
            simpleDataValidationRules={CheckGivenSupplierName}
          />
        </FormItem>
        <FormItem
          label="Reference"
          valueAdd={{
            fieldName: 'supplierReferenceNumber',
            ...dataSourceTracking?.supplierReferenceNumber
          }}
        >
          <FormInput.Text
            label="Reference"
            name="data.supplierReferenceNumber"
            simpleDataValidationRules={
              CheckReceiverOrSupplierOrSenderOrCarrierReference
            }
          />
        </FormItem>
        <FormItem
          label="Certifications"
          valueAdd={{
            fieldName: 'givenSupplierCertifications',
            ...dataSourceTracking?.givenSupplierCertifications
          }}
          multipleInfractions={true}
          infractionPrefix="data.givenSupplierCertifications"
        >
          <div style={{ display: 'flex', gap: 16 }}>
            <Checkbox
              name="bes6001Cert"
              value={bes6001Cert}
              onChange={v => {
                setBes6001Cert(v)
                form.setValue('data.BES6001', v, { shouldDirty: true })
              }}
            >
              BES6001
            </Checkbox>
            <Checkbox
              name="caresCert"
              value={caresCert}
              onChange={v => {
                setCaresCert(v)
                form.setValue('data.CARES', v, { shouldDirty: true })
              }}
            >
              CARES
            </Checkbox>
            <Checkbox
              name="iso14001Cert"
              value={iso14001Cert}
              onChange={v => {
                setIso14001Cert(v)
                form.setValue('data.ISO14001', v, { shouldDirty: true })
              }}
            >
              ISO 14001
            </Checkbox>
            <Checkbox
              name="fscCert"
              value={fscCert}
              onChange={v => {
                setFscCert(v)
                form.setValue('data.FSC', v, { shouldDirty: true })
              }}
            >
              FSC
            </Checkbox>
            <Checkbox
              name="pefcCert"
              value={pefcCert}
              onChange={v => {
                setPefcCert(v)
                form.setValue('data.PEFC', v, { shouldDirty: true })
              }}
            >
              PEFC
            </Checkbox>
          </div>
          {Array.isArray(supplierCertificateInfractions) &&
            supplierCertificateInfractions.length > 0 &&
            supplierCertificateInfractions.map((inf, idx) => (
              <ErrorContainer key={idx}>
                <FormInfraction infraction={inf} />
              </ErrorContainer>
            ))}
        </FormItem>
        <FormItem
          label="Trade contractor"
          valueAdd={{
            fieldName: 'tradeContractor',
            ...dataSourceTracking?.tradeContractor
          }}
        >
          <FormInput.Text
            label="Trade contractor"
            name="data.tradeContractor"
            simpleDataValidationRules={CheckTradeContractor}
          />
        </FormItem>
        <FormItem
          label="Vehicle registration"
          valueAdd={{
            fieldName: 'vehicleRegistration',
            ...dataSourceTracking?.vehicleRegistration
          }}
        >
          <FormInput.Text
            label="Vehicle registration"
            name="data.vehicleRegistration"
            simpleDataValidationRules={CheckVehicleReg}
          />
        </FormItem>
        <FormItem
          label="External reference"
          valueAdd={{
            fieldName: 'clientReference',
            ...dataSourceTracking?.clientReference
          }}
        >
          <FormInput.Text
            label="External reference"
            name="data.clientReference"
            simpleDataValidationRules={
              CheckReceiverOrSupplierOrSenderOrCarrierReference
            }
          />
        </FormItem>
        <FormItem
          label="Notes"
          valueAdd={{
            fieldName: 'notes',
            ...dataSourceTracking?.notes
          }}
        >
          <FormInput.TextArea
            label="Notes"
            name="data.notes"
            simpleDataValidationRules={CheckNotes}
          />
        </FormItem>
      </SubSection>
    </>
  )
}

Delivery.transformToFormData = (item, projectTimeZone) =>
  produce(item, draft => {
    draft.data.deliveryArrivalDate =
      Input.DateTimeCells.getDateTimeInputValuesFromISOString(
        item.data.deliveryArrivalDate,
        projectTimeZone
      )
    draft.data.deliveryExitDate =
      Input.DateTimeCells.getDateTimeInputValuesFromISOString(
        item.data.deliveryExitDate,
        projectTimeZone
      )
  })

Delivery.transformFromFormData = (formItem, projectTimeZone) =>
  produce(formItem, draft => {
    draft.data.deliveryArrivalDate = Input.DateTimeCells.getISOStringFromValues(
      formItem.data.deliveryArrivalDate,
      projectTimeZone
    )
    draft.data.deliveryExitDate = Input.DateTimeCells.getISOStringFromValues(
      formItem.data.deliveryExitDate,
      projectTimeZone
    )

    const certifications = []
    if (formItem.data.BES6001) certifications.push(BES6001)
    if (formItem.data.ISO14001) certifications.push(ISO14001)
    if (formItem.data.FSC) certifications.push(FSC)
    if (formItem.data.PEFC) certifications.push(PEFC)
    if (formItem.data.CARES) certifications.push(CARES)

    let givenCerts = formItem.data.givenSupplierCertifications || ''
    givenCerts = givenCerts.replace(BES6001, '')
    givenCerts = givenCerts.replace(ISO14001, '')
    givenCerts = givenCerts.replace(CARES, '')
    givenCerts = givenCerts.replace(PEFC, '')
    givenCerts = givenCerts.replace(FSC, '')
    draft.data.givenSupplierCertifications =
      givenCerts + certifications.join('')
  })
